/**
 * Centralized auth/transport helper for the chat surface. Mirrors the host
 * `useApi` contract (Authorization: Bearer <id_token>) but is owned by the chat
 * module so the chat-specific `x-site-id` header is constructed in exactly one
 * place. (Note: most data APIs use `x-site`; the chat controller uses `x-site-id`
 * — keeping this in one helper prevents that easy-to-regress discrepancy.)
 */
export function useChatHttp() {
  const config = useRuntimeConfig();
  const { session } = useUserSession();

  const apiUrl = (): string => config.public.apiUrl as string;

  /** Bearer auth header value, derived from the session id token. Throws if signed out. */
  function bearer(): string {
    const token = (session.value?.tokens as { id_token?: string } | undefined)?.id_token;
    if (!token) {
      throw new Error("No ID token is available. User is not logged in.");
    }
    return `Bearer ${token}`;
  }

  /** Headers for chat-surface endpoints: bearer auth + the chat site header. */
  function authHeaders(
    siteId: string | undefined,
    extra?: Record<string, string>,
  ): Record<string, string> {
    if (!siteId) {
      throw new Error("A site id is required to use chat.");
    }
    return {
      Authorization: bearer(),
      "x-site-id": siteId,
      ...extra,
    };
  }

  return { apiUrl, bearer, authHeaders };
}
